import React, { useEffect, useState } from "react";
import {
  Card,
  Select,
  Form,
  Input,
  InputNumber,
  Button,
  Table,
  Space,
  Tag,
  Popconfirm,
  message,
  Modal,
  Empty,
  Row,
  Col,
} from "antd";
import SchoolApi from "./api";

// 409 -> bağımlı kayıt (öğrenci/şube) var; kullanıcıya "yine de sil" sorusu
const handleDeleteConflict = (e, { title, onForce }) => {
  const status = e?.response?.status;
  const msg = e?.response?.data?.message || "Silinemedi";
  if (status === 409) {
    Modal.confirm({
      title,
      content: `${msg}\n\nYine de silmek istiyor musunuz? Bağlı öğrenci atamaları ve şubeler de pasife alınır.`,
      okText: "Yine de sil",
      okButtonProps: { danger: true },
      cancelText: "Vazgeç",
      onOk: onForce,
    });
  } else {
    message.error(msg);
  }
};

const SchoolBranches = () => {
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState(null);
  const [grades, setGrades] = useState([]);
  const [branchesByGrade, setBranchesByGrade] = useState({});
  const [loading, setLoading] = useState(false);
  const [gradeForm] = Form.useForm();
  const [branchForm] = Form.useForm();
  const [branchGradeId, setBranchGradeId] = useState(null);

  useEffect(() => {
    SchoolApi.listSchools()
      .then((d) => setSchools((d || []).filter((s) => s.status !== 3)))
      .catch(() => message.error("Okullar getirilemedi"));
  }, []);

  const loadTree = (id) => {
    if (!id) return;
    setLoading(true);
    Promise.all([SchoolApi.listGrades(id), SchoolApi.listAllBranches(id)])
      .then(([gr, br]) => {
        setGrades(gr || []);
        const map = {};
        (br || []).forEach((b) => {
          (map[b.grade_id] = map[b.grade_id] || []).push(b);
        });
        setBranchesByGrade(map);
      })
      .catch(() => message.error("Sınıf/şube listesi getirilemedi"))
      .finally(() => setLoading(false));
  };

  const onSchoolChange = (id) => {
    setSchoolId(id);
    setGrades([]);
    setBranchesByGrade({});
    loadTree(id);
  };

  const addGrade = (values) => {
    SchoolApi.createGrade(schoolId, { name: values.name?.trim(), sort_order: values.sort_order || 0 })
      .then(() => {
        message.success("Sınıf eklendi");
        gradeForm.resetFields();
        loadTree(schoolId);
      })
      .catch((e) => message.error(e?.response?.data?.message || "Sınıf eklenemedi"));
  };

  const deleteGrade = (g, force = false) => {
    SchoolApi.deleteGrade(g.id, force)
      .then(() => {
        message.success("Sınıf silindi");
        loadTree(schoolId);
      })
      .catch((e) =>
        handleDeleteConflict(e, {
          title: `"${g.name}" seviyesini sil`,
          onForce: () => deleteGrade(g, true),
        })
      );
  };

  const addBranch = (values) => {
    if (!branchGradeId) return message.warning("Önce sınıf seçin");
    SchoolApi.createBranch(branchGradeId, { name: values.name?.trim() })
      .then(() => {
        message.success("Şube eklendi");
        branchForm.resetFields();
        loadTree(schoolId);
      })
      .catch((e) => message.error(e?.response?.data?.message || "Şube eklenemedi"));
  };

  const deleteBranch = (b, force = false) => {
    SchoolApi.deleteBranch(b.branch_id, force)
      .then(() => {
        message.success("Şube silindi");
        loadTree(schoolId);
      })
      .catch((e) =>
        handleDeleteConflict(e, {
          title: `"${b.branch_name}" şubesini sil`,
          onForce: () => deleteBranch(b, true),
        })
      );
  };

  const gradeColumns = [
    { title: "#", dataIndex: "id", width: 70 },
    { title: "Sınıf / Seviye", dataIndex: "name" },
    { title: "Sıra", dataIndex: "sort_order", width: 80 },
    {
      title: "Şubeler",
      key: "branches",
      render: (_, g) => {
        const list = branchesByGrade[g.id] || [];
        if (!list.length) return <span style={{ color: "#999" }}>Şube yok</span>;
        return (
          <Space wrap>
            {list.map((b) => (
              <Popconfirm
                key={b.branch_id}
                title={`"${b.branch_name}" şubesi silinsin mi?`}
                onConfirm={() => deleteBranch(b)}
                okText="Evet"
                cancelText="Hayır"
              >
                <Tag color="blue" style={{ cursor: "pointer" }} closable onClose={(e) => e.preventDefault()}>
                  {b.branch_name} ({b.student_count})
                </Tag>
              </Popconfirm>
            ))}
          </Space>
        );
      },
    },
    {
      title: "İşlem",
      width: 200,
      render: (_, g) => (
        <Space>
          <Button
            size="small"
            type={branchGradeId === g.id ? "primary" : "default"}
            onClick={() => setBranchGradeId(g.id)}
          >
            Şube ekle
          </Button>
          <Popconfirm title="Sınıf silinsin mi?" onConfirm={() => deleteGrade(g)} okText="Evet" cancelText="Hayır">
            <Button size="small" danger>
              Sil
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="container-fluid p-3">
      <h1 className="h3 mb-3">
        <strong>Sınıf / Şube Ata</strong>
      </h1>

      <Card style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <label>Okul seçin</label>
          <Select
            showSearch
            style={{ width: 360, maxWidth: "100%" }}
            placeholder="Okul seçin"
            optionFilterProp="children"
            value={schoolId}
            onChange={onSchoolChange}
            options={schools.map((s) => ({ value: s.id, label: s.code ? `${s.name} (${s.code})` : s.name }))}
          />
        </Space>
      </Card>

      {!schoolId ? (
        <Empty description="Devam etmek için bir okul seçin" />
      ) : (
        <>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={24} md={12}>
              <Card title="Sınıf / Seviye Ekle" size="small">
                <Form form={gradeForm} layout="inline" onFinish={addGrade}>
                  <Form.Item name="name" rules={[{ required: true, message: "Zorunlu" }]}>
                    <Input placeholder='Örn. "5. Sınıf" / "Hazırlık"' style={{ width: 220 }} />
                  </Form.Item>
                  <Form.Item name="sort_order">
                    <InputNumber placeholder="Sıra" min={0} />
                  </Form.Item>
                  <Button type="primary" htmlType="submit">
                    Ekle
                  </Button>
                </Form>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card
                title={
                  branchGradeId
                    ? `Şube Ekle — ${grades.find((g) => g.id === branchGradeId)?.name || ""}`
                    : "Şube Ekle (önce sınıf seçin)"
                }
                size="small"
              >
                <Form form={branchForm} layout="inline" onFinish={addBranch}>
                  <Form.Item name="name" rules={[{ required: true, message: "Zorunlu" }]}>
                    <Input placeholder='Örn. "A" / "B"' style={{ width: 200 }} disabled={!branchGradeId} />
                  </Form.Item>
                  <Button type="primary" htmlType="submit" disabled={!branchGradeId}>
                    Ekle
                  </Button>
                </Form>
              </Card>
            </Col>
          </Row>

          <Card title={`Sınıflar & Şubeler (${grades.length})`}>
            <Table rowKey="id" size="small" loading={loading} dataSource={grades} columns={gradeColumns} pagination={false} />
          </Card>
        </>
      )}
    </div>
  );
};

export default SchoolBranches;
